import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useVendorAccess } from "@/hooks/useVendorAccess";
import { formatPriceWithCurrency } from "@/utils/currency";
import { useCurrency } from "@/hooks/useCurrency";
import { DollarSign, TrendingUp, TrendingDown, Wallet, Download, RefreshCw, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { toast } from "sonner";

interface VendorBalance {
  available_amount_cents: number;
  pending_amount_cents: number;
  total_earned_cents: number;
  total_withdrawn_cents: number;
  currency: string;
}

interface VendorTransaction {
  id: string;
  type: 'credit' | 'debit' | 'withdrawal' | 'refund' | 'adjustment';
  gross_amount_cents: number;
  commission_rate: number | null;
  commission_amount_cents: number | null;
  net_amount_cents: number;
  currency: string;
  source: 'order' | 'refund' | 'withdrawal' | 'adjustment' | 'manual';
  order_id: string | null;
  description: string | null;
  created_at: string;
}

export const VendorBalance = () => {
  const { user } = useAuth();
  const { vendorIds, loading: accessLoading } = useVendorAccess();
  const { displayCurrency, convertPrice } = useCurrency();
  const [transactions, setTransactions] = useState<VendorTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  // Calculer les soldes dynamiquement depuis les transactions (source de vérité)
  // Regroupé par devise pour un affichage précis
  const calculatedBalances = useMemo(() => {
    const balances: Record<string, {
      earned: number;
      withdrawn: number;
      available: number;
    }> = {};
    
    // Regrouper les transactions par devise
    transactions.forEach(t => {
      if (!balances[t.currency]) {
        balances[t.currency] = { earned: 0, withdrawn: 0, available: 0 };
      }
      
      if (t.type === 'credit') {
        balances[t.currency].earned += t.net_amount_cents;
      } else if (t.type === 'withdrawal') {
        balances[t.currency].withdrawn += Math.abs(t.net_amount_cents);
      }
    });
    
    // Calculer disponible pour chaque devise
    Object.keys(balances).forEach(currency => {
      balances[currency].available = balances[currency].earned - balances[currency].withdrawn;
    });
    
    // Consolider tout en une seule devise (la devise d'affichage de l'utilisateur)
    let totalAvailable = 0;
    let totalEarned = 0;
    let totalWithdrawn = 0;
    
    Object.entries(balances).forEach(([currency, amounts]) => {
      totalAvailable += convertPrice(amounts.available, currency as any, displayCurrency);
      totalEarned += convertPrice(amounts.earned, currency as any, displayCurrency);
      totalWithdrawn += convertPrice(amounts.withdrawn, currency as any, displayCurrency);
    });
    
    return {
      available_amount_cents: Math.round(totalAvailable),
      pending_amount_cents: 0,
      total_earned_cents: Math.round(totalEarned),
      total_withdrawn_cents: Math.round(totalWithdrawn),
      currency: displayCurrency
    };
  }, [transactions, displayCurrency, convertPrice]);

  useEffect(() => {
    if (user?.id && !accessLoading && vendorIds.length > 0) {
      fetchTransactions();
    }
  }, [user?.id, accessLoading, vendorIds]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('vendor_transactions')
        .select('*')
        .in('vendor_id', vendorIds)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) {
        console.error('Error fetching transactions:', error);
        toast.error('Erreur lors du chargement des transactions');
        return;
      }

      setTransactions((data || []) as VendorTransaction[]);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    await fetchTransactions();
    toast.success('Données actualisées');
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'credit':
        return <ArrowUpRight className="h-4 w-4 text-green-600" />;
      case 'debit':
      case 'withdrawal':
        return <ArrowDownRight className="h-4 w-4 text-red-600" />;
      case 'refund':
        return <ArrowUpRight className="h-4 w-4 text-orange-600" />;
      default:
        return <RefreshCw className="h-4 w-4 text-gray-600" />;
    }
  };

  const getTransactionLabel = (type: string, source: string) => {
    if (type === 'credit' && source === 'order') return 'Vente';
    if (type === 'withdrawal') return 'Retrait';
    if (type === 'refund') return 'Remboursement';
    if (type === 'adjustment') return 'Ajustement';
    return type;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="flex items-center justify-center">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Utiliser les soldes calculés dynamiquement
  const availableAmount = calculatedBalances.available_amount_cents;
  const pendingAmount = calculatedBalances.pending_amount_cents;
  const totalEarned = calculatedBalances.total_earned_cents;
  const totalWithdrawn = calculatedBalances.total_withdrawn_cents;

  return (
    <div className="space-y-6">
      {/* Balance Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Solde disponible</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatPriceWithCurrency(availableAmount, displayCurrency, displayCurrency, (amount) => amount)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Disponible pour retrait
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">En attente</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {formatPriceWithCurrency(pendingAmount, displayCurrency, displayCurrency, (amount) => amount)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Commandes non retirées
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Gains totaux</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatPriceWithCurrency(totalEarned, displayCurrency, displayCurrency, (amount) => amount)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Net après commission
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Retraits</CardTitle>
            <TrendingDown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatPriceWithCurrency(totalWithdrawn, displayCurrency, displayCurrency, (amount) => amount)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Total retiré
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Transactions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Historique des transactions</CardTitle>
              <CardDescription>
                Toutes vos transactions financières
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleRefresh}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Actualiser
              </Button>
              <Button size="sm" disabled>
                <Download className="h-4 w-4 mr-2" />
                Exporter
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Aucune transaction pour le moment</p>
              <p className="text-sm text-muted-foreground mt-1">
                Vos gains apparaîtront ici après vos premières ventes
              </p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Montant brut</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                    <TableHead className="text-right">Montant net</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell className="font-medium">
                        {new Date(transaction.created_at).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getTransactionIcon(transaction.type)}
                          <span className="capitalize">
                            {getTransactionLabel(transaction.type, transaction.source)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {transaction.order_id ? (
                          <Badge variant="outline">Commande</Badge>
                        ) : (
                          <Badge variant="secondary">{transaction.source}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatPriceWithCurrency(
                          transaction.gross_amount_cents,
                          transaction.currency,
                          displayCurrency,
                          convertPrice
                        )}
                      </TableCell>
                      <TableCell className="text-right text-red-600">
                        {transaction.commission_amount_cents ? (
                          <>
                            -{formatPriceWithCurrency(
                              transaction.commission_amount_cents,
                              transaction.currency,
                              displayCurrency,
                              convertPrice
                            )}
                            {transaction.commission_rate && (
                              <span className="text-xs text-muted-foreground ml-1">
                                ({(transaction.commission_rate * 100).toFixed(0)}%)
                              </span>
                            )}
                          </>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell className={`text-right font-bold ${
                        transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {transaction.type === 'credit' ? '+' : '-'}
                        {formatPriceWithCurrency(
                          Math.abs(transaction.net_amount_cents),
                          transaction.currency,
                          displayCurrency,
                          convertPrice
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
